// The header CPS reads a request's correlation reference from. It mints one
// itself only when this is absent, so sending ours is what ties the portal's
// half of a payment to the backend's log lines.
export const CORRELATION_ID_HEADER = "X-Correlation-ID";

const UUID_LAYOUT = [4, 2, 2, 2, 6];

type RandomSource = { getRandomValues?: (bytes: Uint8Array) => Uint8Array };

const fill = (bytes: Uint8Array): Uint8Array => {
  const source = (globalThis as { crypto?: RandomSource }).crypto;

  if (typeof source?.getRandomValues === "function") {
    return source.getRandomValues(bytes);
  }

  // Not a secret. It identifies one request in a log, and a deployment served
  // over plain http has no Web Crypto at all — an absent reference costs more
  // than a guessable one.
  for (let index = 0; index < bytes.length; index += 1) {
    bytes[index] = Math.floor(Math.random() * 256);
  }

  return bytes;
};

const uuidV4 = (): string => {
  const bytes = fill(new Uint8Array(16));

  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, "0"));

  let offset = 0;
  return UUID_LAYOUT.map(length => {
    const group = hex.slice(offset, offset + length).join("");
    offset += length;
    return group;
  }).join("-");
};

// D-16: one fresh reference per outbound request, generated unconditionally.
// There is no "only when the caller omits one" path — the portal is the party
// that always knows the value, which is what makes D-17 (a reference on every
// outcome, success included) possible at all.
export const getCorrelationId = (): string => {
  const source = (globalThis as { crypto?: { randomUUID?: () => string } })
    .crypto;

  return typeof source?.randomUUID === "function"
    ? source.randomUUID()
    : uuidV4();
};

export default getCorrelationId;
